import { findNodeById, CANVAS_ID } from "../../utils/svgParser";

function NumberField({ label, value, onChange, step = 1, onBeginBatch, onEndBatch }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#D7C7A1]/60 w-14 shrink-0">{label}</span>
      <input
        type="number"
        step={step}
        value={value}
        onFocus={onBeginBatch}
        onBlur={onEndBatch}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-1 text-sm text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
      />
    </div>
  );
}

function ColorField({ label, value, onChange }) {
  const isHexLike = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value || "");
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#D7C7A1]/60 w-14 shrink-0">{label}</span>
      <label
        className="w-7 h-7 rounded border border-[#145A43] shrink-0 cursor-pointer overflow-hidden relative"
        style={{
          backgroundColor: isHexLike ? value : "transparent",
          backgroundImage: isHexLike
            ? "none"
            : "linear-gradient(45deg, #444 25%, transparent 25%), linear-gradient(-45deg, #444 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #444 75%), linear-gradient(-45deg, transparent 75%, #444 75%)",
          backgroundSize: "6px 6px",
          backgroundPosition: "0 0, 0 3px, 3px -3px, -3px 0",
        }}
      >
        <input
          type="color"
          value={isHexLike ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
      </label>
      <input
        type="text"
        value={value ?? ""}
        placeholder="none / #hex / url(#id)"
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-1 text-xs text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
      />
    </div>
  );
}

function CanvasProperties({ canvasSize, backgroundColor, onSizeChange, onBackgroundChange }) {
  const isTransparent = backgroundColor === null;

  return (
    <div className="w-72 shrink-0 bg-[#0F0F0F] border-l border-[#145A43]/40 flex flex-col overflow-y-auto">
      <div className="px-4 py-3 border-b border-[#145A43]/40">
        <h2 className="text-[#D7C7A1] text-sm font-medium">Canvas</h2>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Position</p>
          <div className="flex items-center gap-2">
            <input
              disabled
              value={0}
              className="w-full bg-[#0F0F0F] border border-[#145A43]/40 rounded px-2 py-1 text-sm text-[#D7C7A1]/40 cursor-not-allowed"
            />
            <input
              disabled
              value={0}
              className="w-full bg-[#0F0F0F] border border-[#145A43]/40 rounded px-2 py-1 text-sm text-[#D7C7A1]/40 cursor-not-allowed"
            />
          </div>
          <p className="text-[10px] text-[#D7C7A1]/30 mt-1">Origin canvas tetap di 0, 0</p>
        </div>

        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Size</p>
          <div className="flex items-center gap-2">
            <NumberField
              label="W"
              value={canvasSize.width}
              onChange={(v) => onSizeChange(Math.max(1, v), canvasSize.height)}
            />
            <NumberField
              label="H"
              value={canvasSize.height}
              onChange={(v) => onSizeChange(canvasSize.width, Math.max(1, v))}
            />
          </div>
        </div>

        <div className="pt-2 border-t border-[#145A43]/40">
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Canvas Color</p>
          <div className="flex items-center gap-2">
            <label
              className="w-8 h-8 rounded border border-[#145A43] overflow-hidden cursor-pointer relative shrink-0"
              style={{
                backgroundColor: !isTransparent ? backgroundColor : "transparent",
                backgroundImage: isTransparent
                  ? "linear-gradient(45deg, #444 25%, transparent 25%), linear-gradient(-45deg, #444 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #444 75%), linear-gradient(-45deg, transparent 75%, #444 75%)"
                  : "none",
                backgroundSize: "6px 6px",
                backgroundPosition: "0 0, 0 3px, 3px -3px, -3px 0",
              }}
            >
              <input
                type="color"
                value={isTransparent ? "#ffffff" : backgroundColor}
                onChange={(e) => onBackgroundChange(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
            </label>
            <input
              type="text"
              value={isTransparent ? "none" : backgroundColor}
              onChange={(e) => {
                const v = e.target.value;
                onBackgroundChange(v === "none" || v === "" ? null : v);
              }}
              className="flex-1 bg-[#0F0F0F] border border-[#145A43] rounded px-2 py-1 text-xs text-[#D7C7A1] focus:outline-none focus:border-[#2F8F6F]"
            />
          </div>
          <button
            onClick={() => onBackgroundChange(null)}
            className={`mt-2 w-full text-xs py-1.5 rounded transition-colors ${
              isTransparent
                ? "bg-[#2F8F6F] text-[#0F0F0F]"
                : "bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] hover:text-[#0F0F0F]"
            }`}
          >
            Transparan
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PropertiesPanel({
  selectedId,
  elementTree,
  onChange,
  onAttrChange,
  onAddKeyframe,
  canvasSize,
  backgroundColor,
  onCanvasSizeChange,
  onCanvasBackgroundChange,
  onBeginBatch,
  onEndBatch,
}) {
  if (selectedId === CANVAS_ID) {
    return (
      <CanvasProperties
        canvasSize={canvasSize}
        backgroundColor={backgroundColor}
        onSizeChange={onCanvasSizeChange}
        onBackgroundChange={onCanvasBackgroundChange}
      />
    );
  }

  const node = selectedId ? findNodeById(elementTree, selectedId) : null;

  if (!node) {
    return (
      <div className="w-72 shrink-0 bg-[#0F0F0F] border-l border-[#145A43]/40 p-4">
        <p className="text-xs text-[#D7C7A1]/50">Pilih elemen untuk edit properti.</p>
      </div>
    );
  }

  const t = node.transform;

  const field = (key, label, step) => (
    <NumberField
      label={label}
      value={t[key]}
      step={step}
      onChange={(v) => onChange(key, v)}
      onBeginBatch={onBeginBatch}
      onEndBatch={onEndBatch}
    />
  );

  return (
    <div className="w-72 shrink-0 bg-[#0F0F0F] border-l border-[#145A43]/40 flex flex-col overflow-y-auto">
      <div className="px-4 py-3 border-b border-[#145A43]/40">
        <h2 className="text-[#D7C7A1] text-sm font-medium truncate">{node.id}</h2>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Position</p>
          <div className="space-y-2">
            {field("x", "X")}
            {field("y", "Y")}
          </div>
        </div>

        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Rotate</p>
          {field("rotate", "deg")}
        </div>

        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Scale</p>
          <div className="space-y-2">
            {field("scaleX", "X", 0.1)}
            {field("scaleY", "Y", 0.1)}
          </div>
        </div>

        <div>
          <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Skew</p>
          <div className="space-y-2">
            {field("skewX", "X")}
            {field("skewY", "Y")}
          </div>
        </div>

        {onAttrChange && (
          <div>
            <p className="text-xs text-[#2F8F6F] mb-2 uppercase tracking-wide">Color</p>
            <div className="space-y-2">
              <ColorField
                label="Fill"
                value={node.attrs.fill}
                onChange={(v) => onAttrChange(node.id, "fill", v)}
              />
              <ColorField
                label="Stroke"
                value={node.attrs.stroke}
                onChange={(v) => onAttrChange(node.id, "stroke", v)}
              />
            </div>
          </div>
        )}

        {onAddKeyframe && (
          <div className="pt-2 space-y-1.5 border-t border-[#145A43]/40">
            <p className="text-xs text-[#D7C7A1]/50 mb-1">Kunci nilai saat ini sebagai keyframe:</p>
            <button
              onClick={() => onAddKeyframe("position", { x: t.x, y: t.y })}
              className="w-full text-xs py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] transition-colors"
            >
              + Keyframe Position
            </button>
            <button
              onClick={() => onAddKeyframe("rotate", t.rotate)}
              className="w-full text-xs py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] transition-colors"
            >
              + Keyframe Rotate
            </button>
            <button
              onClick={() => onAddKeyframe("scale", { x: t.scaleX, y: t.scaleY })}
              className="w-full text-xs py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] transition-colors"
            >
              + Keyframe Scale
            </button>
            <button
              onClick={() => onAddKeyframe("skew", { x: t.skewX, y: t.skewY })}
              className="w-full text-xs py-1.5 rounded bg-[#145A43] text-[#D7C7A1] hover:bg-[#2F8F6F] transition-colors"
            >
              + Keyframe Skew
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
